import {
  UserIcon,
  ChatAlt2Icon,
  DocumentIcon,
  ExternalLinkIcon,
  InformationCircleIcon,
  PhotographIcon,
  RefreshIcon,
  VideoCameraIcon,
} from "@heroicons/react/outline";
import { StarIcon } from "@heroicons/react/solid";
import React, { useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import {
  addLocalNotification,
  requestGetConsultation,
  requestListConsultation,
  requestListConsultationByConsultant,
  requestListConsultationByUser,
  requestListMessage,
  requestListNextMessage,
  resetConsultationList,
  resetConsultationListError,
  useAuthState,
  useConsultationDispatch,
  useConsultationState,
  useNotificationDispatch,
} from "../../Context";
import {
  cancelAPIRequest,
  chatPath,
  classNames,
  formatIndonesianPhone,
  generateFileName,
  getErrorMessages,
  isLastTokenNull,
} from "../../Utils";
import ChatWindow from "../../Components/ChatWindow";
import { Button } from "../../Components/Button";
import "./index.css";
import { Select } from "../../Components/Select";
import { EmptyList } from "../../Components/EmptyList";
import { ErrorList } from "../../Components/ErrorList";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import Badge from "../../Components/Badge";
import { Modal } from "../../Components/Modal";
import WhatsappIcon from "../../Assets/images/socials/whatsapp.svg";
import { downloadData, getUrl } from "aws-amplify/storage";
import { Alert } from "../../Components/Alert";
import { ModalConfirm } from "../../Components/ModalConfirm";
import { businessServiceOrderStatus } from "../../Constants";

const tabs = [
  { label: "All", value: "ALL", href: "#", current: false },
  { label: "Pending", value: "PENDING", href: "#", current: true },
  { label: "Ongoing", value: "ONGOING", href: "#", current: false },
  { label: "Finished", value: "FINISHED", href: "#", current: false },
];

const statusLabels = [
  { label: "All", value: "ALL" },
  { label: "Pending", value: "PENDING" },
  { label: "Ongoing", value: "ONGOING" },
  { label: "Finished", value: "FINISHED" },
  { label: "Scheduled", value: "SCHEDULED" },
];

const Consultation = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  let location = useLocation();

  const dispatch = useConsultationDispatch();
  const {
    request,
    consultation,
    loading: consultationLoading,
    error: consultationError,
    consultations,
    listError: error,
    listLoading: loading,
    messages,
    nextTokens,
    nextLoading,
    messageNextToken: nextToken,
    messageNextLoading,
    messageNextError: nextError,
  } = useConsultationState();
  const { user } = useAuthState();

  const notificationDispatch = useNotificationDispatch();

  const [selectedTab, setselectedTab] = useState("ALL");
  const [selectedConsultation, setselectedConsultation] = useState(null);
  const [showCustomerInformationModal, setshowCustomerInformationModal] =
    useState(false);
  const [page, setPage] = useState(1);
  const [showFilesModal, setshowFilesModal] = useState(false);

  const [downloading, setdownloading] = useState(false);
  const [downloadProgress, setdownloadProgress] = useState(0);
  const downloadRef = useRef(null);
  const [downloadDocError, setdownloadDocError] = useState(null);
  const [showDownloadErrorModal, setshowDownloadErrorModal] = useState(false);
  const [preview, setpreview] = useState(null);
  const [showServiceInformationModal, setshowServiceInformationModal] =
    useState(false);

  const statusBadgeMap = {
    PENDING: { type: "alert" },
    ONGOING: { type: "info" },
    FINISHED: { type: "success" },
    SCHEDULED: { type: "alert" },
  };

  useEffect(() => {
    if (consultation.id) {
      requestListMessage(dispatch, { id: consultation.id, silent: true });
    }
  }, [consultation.id, dispatch]);

  useEffect(() => {
    const param = {
      limit: 10,
      page: 1,
    };

    if (user.id) {
      if (selectedTab === "ALL") {
        if (user.membership !== "CONSULTANT") {
          requestListConsultationByUser(dispatch, {
            userID: user.id,
            ...param,
          });
        } else {
          requestListConsultationByConsultant(dispatch, {
            consultantOwner: user.id,
            ...param,
          });
        }
      } else {
        requestListConsultation(dispatch, {
          status: selectedTab,
          ...param,
        });
      }
    }
    setPage(1);
    resetConsultationList(dispatch);
  }, [selectedTab, dispatch, user.id, user.membership]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const status = params.get("status");

    if (!status) {
      navigate("/consultation?status=ALL");
    } else if (
      selectedTab !== status &&
      tabs.map((tab) => tab.value).includes(status)
    ) {
      setselectedTab(status);
      setselectedConsultation(null);
    }
  }, [location.search, selectedTab, navigate]);

  const onLoad = (id) => {
    if (request) {
      cancelAPIRequest(request);
    }
    requestGetConsultation(dispatch, {
      id,
    });
  };

  const onLoadMore = () => {
    const nextPage = error ? page : page + 1;
    const param = {
      limit: 10,
      page: nextPage,
      nextToken: nextTokens[nextPage],
    };

    if (user.id && (error || nextTokens[nextPage])) {
      resetConsultationListError(dispatch);
      setPage(nextPage);
      if (selectedTab === "ALL") {
        if (user.membership !== "CONSULTANT") {
          requestListConsultationByUser(dispatch, {
            userID: user.id,
            ...param,
          });
        } else {
          requestListConsultationByConsultant(dispatch, {
            consultantOwner: user.id,
            ...param,
          });
        }
      } else {
        requestListConsultation(dispatch, {
          status: selectedTab,
          ...param,
        });
      }
    }
  };

  const ErrorToast = (error) => {
    const errorMessage =
      error.errors[0]?.message ||
      "Terjadi kesalahan saat mengambil data konsultasi.";
    addLocalNotification(notificationDispatch, {
      id: `fetchError-${Math.floor(Date.now() / 1000)}`,
      type: "warning",
      title: "Error",
      body: errorMessage,
    });
  };

  function renderConsultations(consultation, index) {
    return (
      <div
        className={classNames(
          selectedConsultation?.id === consultation.id
            ? "bg-blue-50 hover:bg-blue-50"
            : "",
          "p-4 hover:bg-gray-50",
        )}
        key={index}
        onClick={() => {
          setselectedConsultation(consultation);
          onLoad(consultation.id);
        }}
      >
        <div className="flex flex-wrap justify-between items-center">
          <div className="font-medium">
            {user.membership === "CONSULTANT"
              ? consultation?.description?.name
              : consultation.consultant?.user?.name || t("Menunggu Konsultan")}
          </div>
          {consultation.createdAt && (
            <div className="text-xs text-slate-600">
              {dayjs(consultation.createdAt)
                .locale(i18n.language)
                .format("DD MMM YYYY • HH:mm")}
            </div>
          )}
        </div>
        <div className="flex flex-wrap justify-between items-center">
          <div className="flex gap-1">
            <span>{t(consultation.category.name)}</span>
            {consultation?.rating && (
              <div className="flex gap-1 items-center">
                <StarIcon className="h-4 w-4 text-yellow-400" />
                <span className="text-slate-600">
                  ({consultation.rating.toFixed(1)})
                </span>
              </div>
            )}
          </div>
          <div>
            {selectedTab === "ALL" && consultation.status && (
              <div className="text-xs text-slate-600">
                <Badge
                  title={
                    statusLabels.find(
                      (label) => label.value === consultation.status,
                    )?.label
                  }
                  type={statusBadgeMap[consultation.status]?.type}
                />
              </div>
            )}
          </div>
        </div>

        <div className="text-slate-600">
          {consultation.description?.description}
        </div>
      </div>
    );
  }

  const renderCustomerInformationModal = () => {
    const { name, description, email, phone } = consultation?.description || {};

    const customerInfo = [
      { label: t("Name"), value: name || "-" },
      { label: t("consultationDescription"), value: description || "-" },
      { label: t("Email"), value: email || "-" },
      {
        label: t("Phone"),
        value:
          phone && phone.length > 10 ? (
            <div className="flex justify-between">
              <span>{formatIndonesianPhone(phone)}</span>
              <a
                href={`https://wa.me/${phone}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <img className="w-5" src={WhatsappIcon} alt="Whatsapp" />
              </a>
            </div>
          ) : (
            "-"
          ),
      },
    ];

    const renderCustomerDetails = () => (
      <div className="flex flex-col">
        <div className="flex gap-10">
          <div className="flex flex-col gap-2 text-sm w-full">
            {customerInfo.map((info, index) => (
              <div key={index} className="flex flex-col">
                <span>{info.label}</span>
                <span className="font-semibold">{info.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );

    return (
      <Modal
        title={t("consultingUserDetail")}
        show={showCustomerInformationModal}
        onClose={() => setshowCustomerInformationModal(false)}
      >
        <div className="flex flex-col gap-4 py-4 px-4">
          {renderCustomerDetails()}
        </div>
      </Modal>
    );
  };

  const renderServiceInformationModal = () => {
    const { id, addOns, businessService, detail, createdAt, status, city } =
      consultation?.businessServiceOrder || {};

    const getStatusDetails = (status) => {
      return businessServiceOrderStatus.find((item) => item.value === status);
    };

    const businessServiceInfo = [
      { label: t("serviceName"), value: businessService?.name },
      {
        label: t("orderDate"),
        value: dayjs(createdAt)
          .locale(i18n.language)
          .format("DD MMM YYYY HH:mm"),
      },
      { label: t("businessEntityName"), value: detail?.companyName || "-" },
      ...(city?.name
        ? [
            {
              label: t("serviceCity"),
              value: city.name,
            },
          ]
        : []),
      {
        label: t("Add-On"),
        value: (
          <div className="mt-1 text-sm text-gray-900">
            {addOns?.items.length > 0 ? (
              <ul>
                {addOns?.items?.map((addon) => (
                  <li key={addon.addOn.id} className="flex">
                    -<span className="ml-1">{t(addon.addOn.name)}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <dd className="mt-1 text-sm text-gray-900">-</dd>
            )}
          </div>
        ),
      },
      {
        label: t("Status"),
        value: (() => {
          const statusDetails = getStatusDetails(status);
          if (statusDetails) {
            return (
              <Badge
                className="font-semibold"
                title={statusDetails.label}
                type={statusDetails.type}
              />
            );
          }
          return <Badge title="Unknown Status" type="secondary" />;
        })(),
      },
      {
        label: t("order"),
        value: (
          <div className="text-sm text-blue-600 underline text-left">
            <a href={`/business-service/order/${id}`}>{t("viewOrder")}</a>
          </div>
        ),
      },
    ];

    const renderServiceDetails = () => (
      <div className="flex flex-col">
        <div className="flex gap-10">
          <div className="flex flex-col gap-2 text-sm w-full">
            {businessServiceInfo.map((info, index) => (
              <div key={index} className="flex flex-col">
                <span>{info.label}</span>
                <span className="font-semibold">{info.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );

    const renderScheduleDetails = () => (
      <div className="flex flex-col">
        <span className="font-medium text-sm">{t("Schedule")}</span>
        <div className="text-sm">
          {t(dayjs(consultation?.schedule?.scheduledAt).format("dddd"))},{" "}
          {dayjs(consultation?.schedule?.scheduledAt)
            .locale(i18n.language)
            .format("DD MMM YYYY")}
        </div>
        <div className="text-sm">
          {dayjs(consultation?.schedule?.scheduledAt).format("HH:mm")} -{" "}
          {dayjs(consultation?.schedule?.scheduledAt)
            .add(consultation?.schedule?.program?.duration, "minutes")
            .format("HH:mm")}
        </div>
        <div className="flex flex-col">
          <span className="font-medium text-sm">{t("Tutoring Program")}</span>
          <div>
            <button
              className="inline-flex"
              onClick={() => {
                setshowServiceInformationModal(false);
                navigate(
                  `/tutoring/detail/${consultation?.schedule?.program?.id}`,
                );
              }}
            >
              <span className="text-sm text-blue-600 underline text-left">
                {consultation?.schedule?.program?.title}
              </span>
            </button>
          </div>
        </div>
      </div>
    );

    return (
      <Modal
        title={t(
          consultation?.schedule
            ? "Scheduled Consultation"
            : "businessServiceOrderDetailTitle",
        )}
        show={showServiceInformationModal}
        onClose={() => setshowServiceInformationModal(false)}
      >
        <div className="flex flex-col gap-4 py-4 px-4">
          {consultation?.schedule && renderScheduleDetails()}
          {consultation?.businessServiceOrder && renderServiceDetails()}
        </div>
      </Modal>
    );
  };

  function renderConsultationSkeleton() {
    return (
      <div className="animate-pulse divide-y divide-slate-900/10 shadow-sm rounded-b-lg text-sm">
        <div className="p-4">
          <div className="h-3 w-36 my-1.5 bg-gray-300 rounded-md"></div>
          <div className="h-3 w-40 my-1.5 bg-gray-300 rounded-md"></div>
          <div className="h-3 w-24 my-1.5 bg-gray-300 rounded-md"></div>
        </div>
        <div className="p-4">
          <div className="h-3 w-36 my-1.5 bg-gray-300 rounded-md"></div>
          <div className="h-3 w-40 my-1.5 bg-gray-300 rounded-md"></div>
          <div className="h-3 w-24 my-1.5 bg-gray-300 rounded-md"></div>
        </div>
      </div>
    );
  }

  function loadMoreMessages() {
    if (nextToken) {
      requestListNextMessage(dispatch, {
        id: consultation.id,
        nextToken,
      });
    }
  }

  const renderListFileModal = () => {
    const filteredMessages = messages.filter(
      (message) => message.image || message.document,
    );

    return (
      <Modal
        title="fileDocMedia"
        show={showFilesModal}
        onClose={() => {
          setshowFilesModal(false);
        }}
      >
        {filteredMessages.length > 0 ? (
          <div className="flex flex-col divide-y">
            {filteredMessages.map((item) => {
              return (
                <button
                  onClick={() => {
                    setshowFilesModal(false);
                    downloadFile(item.document?.key || item.image?.key);
                    setpreview(item.imageUrl);
                  }}
                  key={item.id}
                  className="flex items-center p-4"
                >
                  <div className=" bg-gray-200 rounded-md">
                    {item.document?.key ? (
                      <DocumentIcon className="text-gray-500 w-14 h-14 p-2" />
                    ) : item.imageUrl ? (
                      <div>
                        <img
                          src={item.imageUrl}
                          alt="Banner"
                          className="w-14 h-14 rounded-md"
                        />
                      </div>
                    ) : (
                      <PhotographIcon className="text-gray-500 w-14 h-14 p-2" />
                    )}
                  </div>
                  <div className="flex-grow px-4 text-start">
                    <p className="text-sm font-medium text-gray-800">
                      {generateFileName(item.document?.key || item.image?.key)}
                    </p>
                  </div>
                </button>
              );
            })}
            {nextToken && (
              <div className="pb-4 pt-4">
                {nextError && (
                  <Alert
                    className="mb-4 mx-4"
                    type="error"
                    message={
                      nextError?.message || "Error fetching more messages."
                    }
                    errors={nextError?.errors}
                  />
                )}
                <div className="flex justify-center">
                  <button
                    disabled={messageNextLoading}
                    className="p-2 inline-flex items-center rounded-md sm:rounded-lg bg-slate-100 text-blue-500 disabled:text-gray-300"
                    onClick={() => {
                      loadMoreMessages();
                    }}
                  >
                    <span className="text-sm">{t("Load More")}</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="bg-white p-4">
            <div className="flex justify-center">
              <EmptyList title="fileNotFound" />
            </div>
          </div>
        )}
      </Modal>
    );
  };

  const onDownloadProgress = (progress) => {
    const { transferredBytes, totalBytes } = progress;
    if (!totalBytes) {
      setdownloadProgress(0);
    } else {
      setdownloadProgress(transferredBytes / totalBytes);
    }
  };

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || "download";
    const clickHandler = () => {
      setTimeout(() => {
        URL.revokeObjectURL(url);
        a.removeEventListener("click", clickHandler);
      }, 150);
    };
    a.addEventListener("click", clickHandler, false);
    a.click();
    return a;
  };

  const downloadFile = async (key) => {
    try {
      setdownloading(true);

      const request = downloadData({
        path: ({ identityId }) => `private/${identityId}/${key}`,
        options: {
          useAccelerateEndpoint: true,
          onProgress: onDownloadProgress,
        },
      });

      downloadRef.current = request;
      const result = await request.result;
      const fileName = generateFileName(key);

      downloadBlob(result.body, fileName);
    } catch (downloadError) {
      if (downloadError.message !== "canceled") {
        await handleDownloadError(key);
      }
    } finally {
      setdownloading(false);
      setdownloadProgress(0);
      setpreview(null);
    }
  };

  const handleDownloadError = async (key) => {
    try {
      const urlResult = await getUrl({
        path: ({ identityId }) => `private/${identityId}/${key}`,
        options: {
          useAccelerateEndpoint: true,
        },
      });
      const signedURL = urlResult.url.href;
      window.open(signedURL);
    } catch (signedURLError) {
      setdownloadDocError(signedURLError);
    }
  };

  const renderFileModal = () => (
    <Modal
      title="Download File"
      show={downloading}
      onClose={() => {
        if (downloadRef.current) {
          const downloadTask = downloadRef.current;
          downloadTask.cancel();
        }
        setdownloading(false);
      }}
      footer={
        <div className="p-4 flex flex-row-reverse gap-2 justify-between items-center border-t border-gray-300 bg-gray-50 rounded-b-lg">
          <div className="flex-1 overflow-hidden">
            <div className="w-full">
              <p className="text-xs text-slate-500">{t("Downloading")}</p>
              <div className="w-full mt-1 bg-gray-200 rounded-md sm:rounded-lg dark:bg-gray-700">
                <div
                  className="bg-blue-600 text-xs font-medium text-blue-100 text-center p-0.5 leading-none rounded-full"
                  style={{
                    width: `${Math.round(downloadProgress * 100)}%`,
                  }}
                >
                  {Math.round(downloadProgress * 100)}%
                </div>
              </div>
            </div>
          </div>
        </div>
      }
    >
      <div className="bg-white p-4">
        <div className="flex justify-center">
          {preview ? (
            <img src={preview} className="w-96" alt="preview" />
          ) : (
            <DocumentIcon className="h-24 w-24 text-slate-500" />
          )}
        </div>
      </div>
    </Modal>
  );

  const renderDownloadError = () => {
    if (downloadDocError) {
      return (
        <ModalConfirm
          open={showDownloadErrorModal}
          type="ALERT"
          setOpen={(open) => {
            setshowDownloadErrorModal(open);
          }}
          title="Error"
          description={getErrorMessages(downloadDocError)}
          onConfirm={() => {
            setdownloadDocError(null);
          }}
        />
      );
    }
    return null;
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-4 lg:gap-x-8">
      <div className="relative flex flex-col bg-white shadow-sm rounded-md sm:rounded-lg list-container">
        {/* Categories */}
        {renderFileModal()}
        {renderDownloadError()}
        {renderListFileModal()}
        {renderCustomerInformationModal()}
        {renderServiceInformationModal()}
        <div>
          <div className="sm:hidden flex z-10">
            <Select
              name="tabs"
              className="w-full p-2 shadow-sm"
              value={selectedTab}
              onChange={(e) => {
                setselectedTab(e.target.value);
                navigate("/consultation?status=" + e.target.value);
              }}
              options={tabs}
            />
          </div>
          <div className="hidden sm:block">
            <nav
              className="relative z-0 h-14 items-center rounded-t-lg shadow-sm flex divide-x divide-gray-200"
              aria-label="Tabs"
            >
              {tabs.map((tab, tabIdx) => (
                <button
                  key={tab.value}
                  className={classNames(
                    tab.value === selectedTab
                      ? "text-gray-900"
                      : "text-gray-500 hover:text-gray-700",
                    tabIdx === 0 ? "rounded-tl-lg" : "",
                    tabIdx === tabs.length - 1 ? "rounded-tr-lg" : "",
                    "group relative min-w-0 flex-1 h-full overflow-hidden bg-white py-4 px-4 text-sm font-medium text-center hover:bg-gray-50 focus:z-10",
                  )}
                  disabled={loading}
                  onClick={() => {
                    setselectedTab(tab.value);
                    setselectedConsultation(null);
                    navigate("/consultation?status=" + tab.value);
                  }}
                >
                  <span>{t(tab.label)}</span>
                  <span
                    aria-hidden="true"
                    className={classNames(
                      tab.value === selectedTab
                        ? "bg-blue-500"
                        : "bg-transparent",
                      "absolute inset-x-0 bottom-0 h-0.5",
                    )}
                  />
                </button>
              ))}
            </nav>
          </div>
        </div>
        {/* Consultations */}
        <div className="flex-1 overflow-auto">
          {loading || nextLoading ? (
            renderConsultationSkeleton()
          ) : (
            <div className="h-full flex flex-col divide-y divide-slate-900/10 text-sm">
              {error ? (
                <>
                  {page === 1 ? (
                    <ErrorList
                      className="flex-1"
                      message={
                        error?.message ||
                        "Terjadi kesalahan saat mengambil konsultasi."
                      }
                      errors={error?.errors}
                    />
                  ) : (
                    <>
                      {ErrorToast(error)}
                      {consultations.map((consultation, index) =>
                        renderConsultations(consultation, index),
                      )}
                      {isLastTokenNull(nextTokens) ? null : (
                        <div className="p-4 flex justify-center">
                          <button
                            onClick={onLoadMore}
                            className="text-blue-500 hover:underline"
                          >
                            {t("Load More")}
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </>
              ) : (
                <>
                  {consultations.length > 0 ? (
                    <>
                      {consultations.map((consultation, index) =>
                        renderConsultations(consultation, index),
                      )}
                      {isLastTokenNull(nextTokens) ? null : (
                        <div className="p-4 flex justify-center">
                          <button
                            onClick={onLoadMore}
                            className="text-blue-500 hover:underline"
                          >
                            {t("Load More")}
                          </button>
                        </div>
                      )}
                    </>
                  ) : (
                    <EmptyList
                      className="flex-1"
                      title="Tidak Ada Konsultasi"
                    />
                  )}
                </>
              )}
            </div>
          )}
        </div>
        {/* Order button */}
        <div className="w-full bg-white p-4 flex flex-wrap gap-2 rounded-b-lg shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)]">
          <a href="/consultation/form" className="flex-1">
            <Button
              icon={ChatAlt2Icon}
              className="w-full"
              title="New Consultation"
            />
          </a>
        </div>
      </div>

      {selectedConsultation?.id ? (
        <div className="relative list-container">
          <ChatWindow
            className="h-full w-full shadow-sm rounded-md sm:rounded-lg"
            consultation={consultation}
            renderActions={() => {
              if (!selectedConsultation?.id) return null;
              return (
                <div className="flex items-center justify-end gap-x-2 mx-4">
                  <button onClick={() => setshowFilesModal(true)}>
                    <DocumentIcon
                      className={classNames(
                        consultationLoading ? "text-gray-500" : "text-blue-500",
                        "w-6 h-6",
                      )}
                    />
                  </button>
                  {user.membership === "CONSULTANT" && (
                    <button
                      onClick={() => setshowCustomerInformationModal(true)}
                      disabled={consultationLoading || consultationError}
                    >
                      <UserIcon
                        className={classNames(
                          consultationLoading || consultationError
                            ? "text-gray-500"
                            : "text-blue-500",
                          "w-6 h-6",
                        )}
                      />
                    </button>
                  )}
                  {(consultation?.schedule ||
                    consultation?.businessServiceOrder) && (
                    <button
                      onClick={() => setshowServiceInformationModal(true)}
                      disabled={loading || error}
                    >
                      <InformationCircleIcon
                        className={classNames(
                          loading || error ? "text-gray-500" : "text-blue-500",
                          "w-6 h-6",
                        )}
                      />
                    </button>
                  )}
                  {selectedConsultation?.status === "ONGOING" &&
                    selectedConsultation?.callEnabled && (
                      <button
                        onClick={() => {
                          if (selectedConsultation?.id) {
                            navigate(
                              `${chatPath(selectedConsultation)}?video=true&call=true`,
                            );
                          }
                        }}
                        disabled={consultationLoading || consultationError}
                      >
                        <VideoCameraIcon
                          className={classNames(
                            consultationLoading || consultationError
                              ? "text-gray-500"
                              : "text-blue-500",
                            "w-6 h-6",
                          )}
                        />
                      </button>
                    )}
                  <button
                    onClick={() => {
                      if (consultation.id) {
                        onLoad(consultation.id);
                      }
                    }}
                    disabled={consultationLoading}
                  >
                    <RefreshIcon
                      className={classNames(
                        consultationLoading ? "text-gray-500" : "text-blue-500",
                        "w-6 h-6",
                      )}
                    />
                  </button>
                  <button
                    onClick={() => {
                      if (consultation.id) {
                        navigate(chatPath(consultation));
                      }
                    }}
                    disabled={consultationLoading || consultationError}
                  >
                    <ExternalLinkIcon
                      className={classNames(
                        consultationLoading || consultationError
                          ? "text-gray-500"
                          : "text-blue-500",
                        "w-6 h-6",
                      )}
                    />
                  </button>
                </div>
              );
            }}
          />
        </div>
      ) : null}
    </div>
  );
};

export default Consultation;
